import React, { FC } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Hyperlink } from '../../../components/Hyperlink';
import { askForDecisionAnalytics } from '../../../decision/decisionUtils';
import { DecisionWatcherButton } from '../search-decision-watcher/DecisionWatcherButton';
import { SearchZeroResultsHelp } from './SearchZeroResultsHelp';
import {
  ZeroResultsContainer,
  ZeroResultsMain,
  ZeroResultsTitle,
} from './ZeroDecisionLib';
import { SearchTypes } from '../../search.types';
import styles from './search_zero_results.module.scss';
import { PrimaryButton, SecondaryButton } from '../../../components/button';
import { CancelWatcherCreationButton } from '../../../watchers/components/cancel-watcher-creation/CancelWatcherCreationButton';

type Props = {
  hasDecisionRef: boolean;
  searchType: SearchTypes;
};

export const ZeroDecisionResults: FC<Props> = ({
  hasDecisionRef,
  searchType,
}) => {
  const source = 'no_result_page';
  const type = hasDecisionRef ? 'primary' : 'secondary';

  const buttons = [
    <DecisionWatcherButton key="0" source="search_decision_zero_result">
      {({
        createWatcher,
        isWatched,
        deleteWatcher,
      }: {
        createWatcher: () => void;
        isWatched: boolean;
        deleteWatcher: () => void;
      }) => (
        <>
          {!isWatched ? (
            <>
              {!hasDecisionRef ? (
                <div className={styles.primary}>
                  <PrimaryButton
                    iconLeft="notifications_none"
                    onClick={createWatcher}
                  >
                    <FormattedMessage id="app.pages.search.zeroResults.addWatcher" />
                  </PrimaryButton>
                </div>
              ) : (
                <div className={styles.secondary}>
                  <SecondaryButton
                    iconLeft="notifications_none"
                    onClick={createWatcher}
                  >
                    <FormattedMessage id="app.pages.search.zeroResults.addWatcher" />
                  </SecondaryButton>
                </div>
              )}
            </>
          ) : (
            <div
              className={classNames({
                [styles.secondary]: hasDecisionRef,
                [styles.primary]: !hasDecisionRef,
              })}
            >
              <CancelWatcherCreationButton
                deleteWatcher={deleteWatcher}
                tooltipId="search-decision-watcher-button-noresult"
              />
            </div>
          )}
        </>
      )}
    </DecisionWatcherButton>,
    <Hyperlink
      key="1"
      href="/getdecision?source=search-zero-results"
      target="_blank"
      data-test-id="ask-a-decision"
      onClick={askForDecisionAnalytics(source, {
        type,
        reportOriginalQueryKey: true,
      })}
      className={classNames(
        'button',
        hasDecisionRef && styles.primary,
        !hasDecisionRef && styles.secondary,
        {
          primary: hasDecisionRef,
          'secondary hollow': !hasDecisionRef,
        },
      )}
    >
      <FormattedMessage id="app.pages.search.zeroResults.askdecision" />
    </Hyperlink>,
  ];
  if (hasDecisionRef) {
    buttons.reverse();
  }

  return (
    <ZeroResultsContainer>
      <span
        className={classNames(
          styles.picto,
          hasDecisionRef ? styles.picto__decision : styles.picto__watcher,
        )}
      />
      <ZeroResultsMain>
        <ZeroResultsTitle>
          <FormattedMessage
            id={
              hasDecisionRef
                ? 'app.pages.search.zeroResults.decisionSearch.title'
                : 'app.pages.search.zeroResults.basicSearch.title'
            }
          />
        </ZeroResultsTitle>
        <p className={styles.message}>
          <FormattedMessage
            id={
              hasDecisionRef
                ? 'app.pages.search.zeroResults.decisionSearch.content'
                : 'app.pages.search.zeroResults.basicSearch.content'
            }
          />
        </p>
        <div className={styles.actions}>{buttons}</div>
        <SearchZeroResultsHelp searchType={searchType} />
      </ZeroResultsMain>
    </ZeroResultsContainer>
  );
};
